import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Layers, Package, Truck, Warehouse, TrendingUp } from 'lucide-react';
import { toast } from 'react-toastify';
import { CardStats, ConfirmModal } from '@components/ui';
import MaterialPageLayout from '../../components/MaterialPageLayout';
import MaterialDetailModal from '../../components/MaterialDetailModal';
import StokSekme from '../../components/StokSekme';
import StokTablosu from '../../components/StokTablosu';
import StokAktarModal from '../../components/StokAktarModal';
import { formatCurrency } from '../../components/StockIndicator';
import ParkeTable from './ParkeTable';
import ParkeModal from './ParkeModal';
import { parkeKategorileri, getParkeByInsaat } from '@mocks/materials/parke';
import { getStokHavuzu } from '@mocks/materials/stokHavuzu';
import { getHareketler, hareketTipleri } from '@mocks/materials/stokHareketleri';
import OdemePlanMiniModal from '@components/shared/OdemePlanMiniModal';
import { addMockCariHareket } from '@mocks/cariHesaplar';

export default function Parke() {
    const { selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);

    const [activeTab, setActiveTab] = useState('kullanim');
    const [filterKategori, setFilterKategori] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [isAktarOpen, setIsAktarOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isOdemePlanOpen, setIsOdemePlanOpen] = useState(false);
    const [odemePlanData, setOdemePlanData] = useState(null);

    const insaatId = showAllConstructions ? null : selectedConstruction?.id;

    const filteredData = useMemo(() => {
        let data = getParkeByInsaat(insaatId);
        if (filterKategori) data = data.filter(item => item.kategori === filterKategori);
        return data;
    }, [insaatId, filterKategori]);

    const stats = useMemo(() => {
        const toplamKutu = filteredData.reduce((acc, s) => acc + s.miktar, 0);
        const teslimKutu = filteredData.filter(s => s.durum === 'teslim').reduce((acc, s) => acc + s.miktar, 0);
        const stokKutu = filteredData.filter(s => s.durum === 'teslim').reduce((acc, s) => acc + s.stokMiktar, 0);
        const toplamMaliyet = filteredData.reduce((acc, s) => acc + s.toplamFiyat, 0);
        return { toplamKutu, teslimKutu, stokKutu, toplamMaliyet };
    }, [filteredData]);

    const stokHavuzuData = getStokHavuzu('parke');
    const hareketlerData = getHareketler('parke');

    // Handlers
    const handleAdd = () => { setSelectedItem(null); setIsModalOpen(true); };
    const handleView = (item) => { setSelectedItem(item); setIsDetailOpen(true); };
    const handleEdit = (item) => { setSelectedItem(item); setIsModalOpen(true); };
    const handleDelete = (item) => { setSelectedItem(item); setIsDeleteOpen(true); };

    const handleSubmit = async (formData, isEdit) => {
        setIsLoading(true);
        await new Promise(resolve => setTimeout(resolve, 800));
        if (!isEdit && formData.supplierId && formData.kdvDahilToplam > 0) {
            addMockCariHareket(formData.supplierId, insaatId || 'c1', { type: 'purchase', subtype: 'fatura', amount: formData.kdvDahilToplam, date: formData.siparisTarihi || new Date().toISOString().split('T')[0], description: formData.autoDescription || `Parke — ${formData.miktar} kutu` });
        }
        toast.success(isEdit ? 'Parke siparişi güncellendi' : 'Yeni parke siparişi oluşturuldu');
        setIsLoading(false);
        setIsModalOpen(false);
        if (!isEdit && formData.supplierId && formData.kdvDahilToplam > 0) { setOdemePlanData({ amount: formData.kdvDahilToplam, companyName: formData.tedarikci }); setIsOdemePlanOpen(true); }
    };

    const handleConfirmDelete = async () => {
        setIsLoading(true);
        await new Promise(resolve => setTimeout(resolve, 500));
        toast.success('Parke siparişi silindi');
        setIsLoading(false);
        setIsDeleteOpen(false);
    };

    const handleAktarim = async (data) => {
        setIsLoading(true);
        await new Promise(resolve => setTimeout(resolve, 500));
        toast.success(`${data.miktar} kutu stok aktarıldı`);
        setIsLoading(false);
        setIsAktarOpen(false);
    };
    const handleOdemePlanConfirm = ({ nextDueDate }) => { toast.success(`Ödeme planı oluşturuldu — Vade: ${nextDueDate}`); setIsOdemePlanOpen(false); setOdemePlanData(null); };

    const statsCards = [
        { title: 'Toplam Sipariş', value: `${stats.toplamKutu} kutu`, icon: Package, color: 'primary' },
        { title: 'Teslim Edilen', value: `${stats.teslimKutu} kutu`, icon: Truck, color: 'success' },
        { title: 'Stok Durumu', value: `${stats.stokKutu} kutu`, icon: Warehouse, color: 'info' },
        { title: 'Toplam Maliyet', value: formatCurrency(stats.toplamMaliyet), icon: TrendingUp, color: 'warning' },
    ];

    const detailFields = [
        { key: 'siparisTarihi', label: 'Sipariş Tarihi', type: 'date' },
        { key: 'tedarikci', label: 'Tedarikçi' },
        { key: 'kategori', label: 'Kategori' },
        { key: 'urunAdi', label: 'Ürün Adı' },
        { key: 'ebat', label: 'Ebat' },
        { key: 'kalinlik', label: 'Kalınlık' },
        { key: 'miktar', label: 'Miktar', type: 'number' },
        { key: 'stokMiktar', label: 'Stok', type: 'number' },
        { key: 'toplamFiyat', label: 'Tutar', type: 'currency' },
        { key: 'durum', label: 'Durum', type: 'status' },
    ];

    const headerFilter = (
        <div className="flex items-center gap-3">
            <StokSekme activeTab={activeTab} onTabChange={setActiveTab} />
            {activeTab === 'kullanim' && (
                <select
                    value={filterKategori}
                    onChange={(e) => setFilterKategori(e.target.value)}
                    className="px-3 py-2 bg-bg-primary border border-border-light rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/50 min-w-[160px]"
                >
                    <option value="">Tüm Kategoriler</option>
                    {parkeKategorileri.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
                </select>
            )}
        </div>
    );

    return (
        <>
            <MaterialPageLayout
                title="Parke"
                subtitle="İnce İşler"
                icon={Layers}
                iconBgColor="bg-tertiary"
                backLink="/insaat/ince"
                stats={statsCards}
                onAddClick={activeTab === 'kullanim' ? handleAdd : undefined}
                headerFilter={headerFilter}
            >
                {activeTab === 'kullanim' ? (
                    <ParkeTable data={filteredData} onView={handleView} onEdit={handleEdit} onDelete={handleDelete} />
                ) : (
                    <StokTablosu
                        stokData={stokHavuzuData}
                        hareketData={hareketlerData}
                        hareketTipleri={hareketTipleri}
                        birim="kutu"
                        onAktarim={() => setIsAktarOpen(true)}
                    />
                )}
            </MaterialPageLayout>

            <ParkeModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSubmit={handleSubmit} initialData={selectedItem} isLoading={isLoading} />
            <MaterialDetailModal isOpen={isDetailOpen} onClose={() => setIsDetailOpen(false)} data={selectedItem} fields={detailFields} title="Parke Siparişi Detayı" />
            <StokAktarModal isOpen={isAktarOpen} onClose={() => setIsAktarOpen(false)} onSubmit={handleAktarim} malzemeTipi="parke" stokData={stokHavuzuData} birim="kutu" isLoading={isLoading} />
            <ConfirmModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={handleConfirmDelete} title="Siparişi Sil" message={`${selectedItem?.urunAdi} siparişini silmek istediğinize emin misiniz?`} isLoading={isLoading} />
            <OdemePlanMiniModal isOpen={isOdemePlanOpen} onClose={() => { setIsOdemePlanOpen(false); setOdemePlanData(null); }} onConfirm={handleOdemePlanConfirm} amount={odemePlanData?.amount} companyName={odemePlanData?.companyName} />
        </>
    );
}
